/**
 * Aperçus du cycle de formation, calculés à la frappe.
 *
 * La règle vit côté serveur, dans `backend/src/domain/cycle-formation.ts` : la
 * découpe en années, la fin proposée et la durée hebdomadaire y sont calculées
 * et c'est elle qui fait foi à l'enregistrement. Ces fonctions n'en sont que
 * l'**aperçu** immédiat, pour que la preview suive la saisie sans aller-retour.
 * Elles travaillent sur des dates civiles `AAAA-MM-JJ`, jamais sur des
 * instants : « du 01/09/2026 au 31/08/2029 » ne dépend pas du fuseau du poste.
 */

export const DUREES_CYCLE = [1, 2, 3, 4] as const;

function versUtc(iso: string): Date | null {
  const lu = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!lu) return null;
  return new Date(Date.UTC(Number(lu[1]), Number(lu[2]) - 1, Number(lu[3])));
}

function versIso(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** Même jour et même mois, `annees` plus tard ; un 29 février passe au 1ᵉʳ mars. */
function plusAnnees(date: Date, annees: number): Date {
  return new Date(Date.UTC(date.getUTCFullYear() + annees, date.getUTCMonth(), date.getUTCDate()));
}

function veille(date: Date): Date {
  return new Date(date.getTime() - 86_400_000);
}

/** Veille de l'anniversaire : 3 ans à partir du 01/09/2026 finissent le 31/08/2029. */
export function finProposee(debut: string, dureeAnnees: number): string | null {
  const date = versUtc(debut);
  return date ? versIso(veille(plusAnnees(date, dureeAnnees))) : null;
}

export interface AnneeCycle {
  rang: number;
  du: string;
  au: string;
}

/** Années d'anniversaire en anniversaire, la dernière bornée par la fin réelle. */
export function anneesDuCycle(debut: string, fin: string): AnneeCycle[] {
  const d = versUtc(debut);
  const f = versUtc(fin);
  if (!d || !f || f < d) return [];
  const annees: AnneeCycle[] = [];
  for (let rang = 1; rang <= 10; rang += 1) {
    const du = plusAnnees(d, rang - 1);
    if (du > f) break;
    const finTheorique = veille(plusAnnees(d, rang));
    annees.push({ rang, du: versIso(du), au: versIso(finTheorique < f ? finTheorique : f) });
  }
  return annees;
}

export function libelleDuree(dureeAnnees: number): string {
  return dureeAnnees === 1 ? '1 an' : `${dureeAnnees} ans`;
}

export function libelleRangAnnee(rang: number): string {
  return rang === 1 ? '1ʳᵉ année' : `${rang}ᵉ année`;
}

/** `AAAA-MM-JJ` → `JJ/MM/AAAA`. */
export function dateFr(iso: string): string {
  const [annee, mois, jour] = iso.split('-');
  return `${jour}/${mois}/${annee}`;
}

/** Écart entre la durée d'une formation et une période réelle, ou `null`. */
export function ecartCycle(dureeAnnees: number | null, debut: string, fin: string): string | null {
  if (!dureeAnnees) return null;
  const annees = anneesDuCycle(debut, fin).length;
  if (annees === 0 || annees === dureeAnnees) return null;
  return annees > dureeAnnees
    ? `La période couvre ${annees} années alors que la formation dure ${libelleDuree(dureeAnnees)}.`
    : `La période ne couvre que ${annees === 1 ? 'une année' : `${annees} années`} alors que la formation dure ${libelleDuree(dureeAnnees)}.`;
}

export interface HorairesSaisis {
  heureDebutMatin: string;
  heureFinMatin: string;
  heureDebutApresMidi: string;
  heureFinApresMidi: string;
  joursParSemaine: number | '';
}

function minutes(heure: string): number | null {
  const lu = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(heure);
  return lu ? Number(lu[1]) * 60 + Number(lu[2]) : null;
}

/** « 35 h 00 ». */
export function libelleDureeHebdomadaire(duree: { heures: number; minutes: number }): string {
  return `${duree.heures} h ${String(duree.minutes).padStart(2, '0')}`;
}

/**
 * Aperçu de la durée hebdomadaire, ou la raison pour laquelle il n'y en a pas.
 * Mêmes règles que `erreurHorairesFormation` côté serveur.
 */
export function apercuDureeHebdomadaire(
  horaires: HorairesSaisis,
): { heures: number; minutes: number } | { erreur: string } | null {
  const champs = [
    horaires.heureDebutMatin,
    horaires.heureFinMatin,
    horaires.heureDebutApresMidi,
    horaires.heureFinApresMidi,
  ];
  const renseignes = champs.filter(Boolean).length;
  if (renseignes === 0 && horaires.joursParSemaine === '') return null;
  if (renseignes < 4 || horaires.joursParSemaine === '') {
    return { erreur: 'Horaires incomplets : renseignez le matin, l’après-midi et le nombre de jours.' };
  }
  const [dm, fm, da, fa] = champs.map(minutes);
  if (dm == null || fm == null || da == null || fa == null) return { erreur: 'Heure illisible (HH:MM).' };
  if (fm <= dm) return { erreur: 'Le matin doit finir après avoir commencé.' };
  if (fa <= da) return { erreur: 'L’après-midi doit finir après avoir commencé.' };
  if (da < fm) return { erreur: 'L’après-midi ne peut pas commencer avant la fin du matin.' };
  const jours = Number(horaires.joursParSemaine);
  if (!Number.isInteger(jours) || jours < 1 || jours > 6) return { erreur: 'De 1 à 6 jours par semaine.' };
  const total = (fm - dm + fa - da) * jours;
  return { heures: Math.floor(total / 60), minutes: total % 60 };
}
